/**
 * 레거시 `businessUploadXLSXHeaderMap` + clientInfo 업로드 검증.
 */
import type { BusinessRelationshipUploadItem } from '@/entities/business/api/business.api';
import {
  PHARMA_TRANSACTION_STATUS_VALUES,
  toPharmaTransactionStatusValue,
} from '@/pages/clients/clientStatusUtils';
import {
  exportXlsxTemplateWithListValidations,
  importFromXlsx,
} from '@/shared/utils/xlsxExport';
import * as XLSX from 'xlsx';

export type { BusinessRelationshipUploadItem };

/** 양식 드롭다운 적용 행 수 (헤더 제외) */
export const BUSINESS_UPLOAD_TEMPLATE_MAX_ROWS = 1000;

/**
 * 거래처 전체 다운로드 엑셀 헤더 (API 필드 → 한글).
 * 키는 export-all BusinessRelationshipCsvResponse 와 맞추고,
 * 열 순서는 운영 가독성 기준(병의원 ID는 병의원명 직전)으로 둔다.
 */
export const BUSINESS_EXPORT_HEADER_MAP: Record<string, string> = {
  relationPharmaCompanyId: '거래처 관계 ID',
  userId: '유저 ID',
  email: '사용자 이메일',
  ceoName: '대표자명',
  companyName: '업체명',
  clinicId: '병의원 ID',
  clinicName: '병의원명',
  clinicTypeCode: '병의원 구분코드',
  clinicType: '병의원 구분',
  clinicClosedOn: '병의원 폐업일',
  pharmacies: '연계 약국',
  pharmaCompanyName: '제약사명',
  transactionStatus: '거래 상태',
  applicationDate: '거래 신청 일자',
  approvalDate: '거래 승인 일자',
  drugCode: '의약품 코드',
  drugName: '의약품명',
  drugApplicationDate: '의약품 신청 일자',
  drugApprovalDate: '의약품 승인 일자',
  drugApprovalStatus: '의약품 승인 상태',
};

export const BUSINESS_UPLOAD_HEADER_MAP: Record<string, string> = {
  userId: '유저 ID*',
  email: '이메일',
  clinicId: '병의원 ID*',
  clinicName: '병의원명',
  parentCompanyId: '상위업체 ID*',
  settlementCompanyId: '정산업체 ID*',
  pharmaCompanyName: '제약사명*',
  drugCode: '의약품 코드*',
  drugName: '의약품명',
  applicationDate: '계약 신청일*',
  approvalDate: '계약 승인일*',
  transactionStatus: '거래 상태',
  drugApplicationDate: '의약품 신청일*',
  drugApprovalDate: '의약품 승인일*',
  totalMonthlyExpectedPrescriptionAmount: '월간 예상 처방 금액*',
};

const DATE_FIELDS = [
  { key: 'applicationDate', label: BUSINESS_UPLOAD_HEADER_MAP.applicationDate },
  { key: 'approvalDate', label: BUSINESS_UPLOAD_HEADER_MAP.approvalDate },
  {
    key: 'drugApplicationDate',
    label: BUSINESS_UPLOAD_HEADER_MAP.drugApplicationDate,
  },
  {
    key: 'drugApprovalDate',
    label: BUSINESS_UPLOAD_HEADER_MAP.drugApprovalDate,
  },
] as const;

const isValidDateString = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

/** YYYYMMDD / Excel serial / Date → YYYY-MM-DD */
export const normalizeExcelDateValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';

  const rawValue = String(value).trim();
  if (/^\d{8}$/.test(rawValue)) {
    return `${rawValue.slice(0, 4)}-${rawValue.slice(4, 6)}-${rawValue.slice(6, 8)}`;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const excelEpoch = Date.UTC(1899, 11, 30);
    const normalizedDate = new Date(excelEpoch + value * 86400 * 1000);
    return normalizedDate.toISOString().slice(0, 10);
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) return rawValue;
  return rawValue;
};

const cellToString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

/**
 * 엑셀 거래 상태 → 서버 한글 표시값.
 * 비우면 undefined(서버 기본 APPROVED). 허용 목록 밖이면 에러.
 */
export const normalizeUploadTransactionStatus = (
  value: unknown,
): string | undefined => {
  const raw = cellToString(value);
  if (!raw) return undefined;

  const normalized = toPharmaTransactionStatusValue(raw);
  if (!normalized) {
    throw new Error(
      `거래 상태는 비워 두거나 다음 중 하나여야 합니다: ${PHARMA_TRANSACTION_STATUS_VALUES.join(', ')}`,
    );
  }
  return normalized;
};

export const filterNonEmptyUploadRows = (
  rows: Record<string, unknown>[],
): Record<string, unknown>[] =>
  rows.filter((row) =>
    Object.values(row).some((value) => String(value ?? '').trim() !== ''),
  );

export const mapRowToBusinessUpload = (
  row: Record<string, unknown>,
): BusinessRelationshipUploadItem => {
  const userId = cellToString(row.userId);
  const clinicId = cellToString(row.clinicId);
  const parentCompanyId = cellToString(row.parentCompanyId);
  const settlementCompanyId = cellToString(row.settlementCompanyId);
  const pharmaCompanyName = cellToString(row.pharmaCompanyName);
  const drugCode = cellToString(row.drugCode);

  if (
    !userId ||
    !clinicId ||
    !parentCompanyId ||
    !settlementCompanyId ||
    !pharmaCompanyName ||
    !drugCode
  ) {
    throw new Error(
      '필수 필드(유저 ID, 병의원 ID, 상위업체 ID, 정산업체 ID, 제약사명, 의약품 코드)가 누락되었습니다.',
    );
  }

  const amount = Number(
    cellToString(row.totalMonthlyExpectedPrescriptionAmount).replace(/,/g, '') ||
      '0',
  );

  const transactionStatus = normalizeUploadTransactionStatus(
    row.transactionStatus,
  );

  return {
    userId: Number(userId),
    email: cellToString(row.email) || undefined,
    clinicId: Number(clinicId),
    clinicName: cellToString(row.clinicName) || undefined,
    parentCompanyId: Number(parentCompanyId),
    settlementCompanyId: Number(settlementCompanyId),
    pharmaCompanyName,
    drugCode,
    drugName: cellToString(row.drugName) || undefined,
    applicationDate: normalizeExcelDateValue(row.applicationDate),
    approvalDate: normalizeExcelDateValue(row.approvalDate),
    ...(transactionStatus ? { transactionStatus } : {}),
    drugApplicationDate: normalizeExcelDateValue(row.drugApplicationDate),
    drugApprovalDate: normalizeExcelDateValue(row.drugApprovalDate),
    totalMonthlyExpectedPrescriptionAmount: amount,
  };
};

/** @deprecated mapRowToBusinessUpload 사용 */
export const mapCsvRowToBusinessUpload = (
  row: Record<string, string>,
): BusinessRelationshipUploadItem => mapRowToBusinessUpload(row);

export const validateUploadDateFields = (
  rows: BusinessRelationshipUploadItem[],
): void => {
  rows.forEach((row, index) => {
    DATE_FIELDS.forEach(({ key, label }) => {
      const value = row[key];
      if (!value || String(value).trim() === '') {
        throw new Error(
          `${index + 2}행 ${label} 값은 필수이며 YYYYMMDD 또는 YYYY-MM-DD 형식이어야 합니다.`,
        );
      }
      if (!isValidDateString(String(value))) {
        throw new Error(
          `${index + 2}행 ${label} 값은 YYYYMMDD 또는 YYYY-MM-DD 형식이어야 합니다. 예: 20251110 또는 2025-11-10`,
        );
      }
    });
  });
};

/** 레거시 clientInfo 업로드: XLSX → payload */
export const parseBusinessUploadXlsx = async (
  file: File,
): Promise<BusinessRelationshipUploadItem[]> => {
  const parsed = await importFromXlsx(file, BUSINESS_UPLOAD_HEADER_MAP, {
    validateOnlyRequiredHeaders: true,
  });
  const cleaned = filterNonEmptyUploadRows(parsed);
  const items = cleaned.map((row, index) => {
    try {
      return mapRowToBusinessUpload(row);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : '업로드 행을 읽지 못했습니다.';
      throw new Error(`${index + 2}행 ${message}`);
    }
  });
  validateUploadDateFields(items);
  return items;
};

/** 거래 상태 콤보박스가 있는 업로드 양식 다운로드 */
export const downloadBusinessUploadTemplate = () => {
  const columnIndex = Object.keys(BUSINESS_UPLOAD_HEADER_MAP).indexOf(
    'transactionStatus',
  );
  const column = XLSX.utils.encode_col(columnIndex);

  return exportXlsxTemplateWithListValidations(
    BUSINESS_UPLOAD_HEADER_MAP,
    '거래처_업로드_양식.xlsx',
    [
      {
        sqref: `${column}2:${column}${BUSINESS_UPLOAD_TEMPLATE_MAX_ROWS + 1}`,
        options: [...PHARMA_TRANSACTION_STATUS_VALUES],
        allowBlank: true,
        promptTitle: '거래 상태',
        promptMessage:
          '신청·확인 중·이관 확인 중·승인·거절·재신청·종료 중 선택. 비우면 승인으로 등록됩니다.',
        errorTitle: '입력 오류',
        errorMessage:
          '거래 상태는 목록에서 선택하거나 비워 두세요. (확인 중·이관 확인 중은 공백 유지)',
      },
    ],
  );
};
